import React, { createContext, useContext, useEffect, useState } from 'react'
import { initializeAnalytics, updateConsentStatus, setUserProperties } from '../../utils/analytics'
import { useAuthStore } from '../../stores/authStore'
import { useLanguageStore } from '../../stores/languageStore'

interface AnalyticsContextType {
  consentGiven: boolean
  giveConsent: () => void
  revokeConsent: () => void
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined)

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider')
  }
  return context
}

interface AnalyticsProviderProps {
  children: React.ReactNode
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [consentGiven, setConsentGiven] = useState<boolean>(() => {
    // Check if user has previously given consent
    const storedConsent = localStorage.getItem('analytics_consent')
    return storedConsent === 'true'
  })
  
  const { user } = useAuthStore()
  const { language } = useLanguageStore()

  // Initialize analytics when consent is given
  useEffect(() => {
    if (consentGiven) {
      initializeAnalytics()
      updateConsentStatus(true)
    } else {
      updateConsentStatus(false)
    }
  }, [consentGiven])

  // Update user properties when user or language changes
  useEffect(() => {
    if (consentGiven && user) {
      // Safely handle the createdAt date
      let signupDate: string | undefined
      if (user.createdAt) {
        try {
          if (user.createdAt instanceof Date) {
            signupDate = user.createdAt.toISOString()
          } else {
            const date = new Date(user.createdAt)
            if (!isNaN(date.getTime())) {
              signupDate = date.toISOString()
            }
          }
        } catch (error) {
          console.warn('Invalid createdAt date for user:', user.id, error)
        }
      }

      setUserProperties({
        user_id: user.id,
        user_type: user.isAdmin || user.role === 'admin' ? 'admin' : 'free',
        country: user.country,
        language: language,
        signup_date: signupDate,
      })
    }
  }, [consentGiven, user, language])

  const giveConsent = () => {
    setConsentGiven(true)
    localStorage.setItem('analytics_consent', 'true')
    updateConsentStatus(true)
  }

  const revokeConsent = () => {
    setConsentGiven(false)
    localStorage.setItem('analytics_consent', 'false')
    updateConsentStatus(false)
  }

  const value = {
    consentGiven,
    giveConsent,
    revokeConsent,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}